import { AssetReference, getProtectedAssetUrl, PatientResponsibility, State, ValidationStatus } from '@uplift/api';
import { CalendarDateService, DateFormats } from '@uplift/services';
import { DateFormats as DateFormatsConstants } from '@uplift/constants';
import { AddressType } from '@uplift/types';
import { centsToDollars } from '../../../Money';
import React, { useCallback, useEffect } from 'react';


export const formatAddress = (addressValues: Omit<AddressType, 'state'>, stateProfile?: State) => {
  const { address1, address2, city, zip } = addressValues;
  const address = [ address1, address2 ].filter(x => x).join(', ');
  const addressCityState = [ city, stateProfile?.fullName, zip ].filter(x => x).join(', ');
  const fullAddress = [ address, addressCityState ].join(', ');

  return { address, addressCityState, fullAddress };
};

export const getDateFormattedForDB = (date: Date | string) => {
  return CalendarDateService.date(date).format(DateFormats.date);
};

export const formatDateFromDB = (date: string) => {
  return CalendarDateService.date(date).format(DateFormatsConstants.DEFAULT_DATE);
};

export const getClientResponsibility = (patientResponsibility: PatientResponsibility, status: ValidationStatus, remainingDeductible: number, coinsurance: number | null | undefined, copay: number) => {
  const showDeductibleRange = remainingDeductible >= 150;
  // client responsibility should show % of allowed if remaining deductible is under 150 and coinsurance percentage is greater than 0
  const showCoinsuranceAllowable = !!coinsurance && (coinsurance * 100) > 0;
  const isInn = status === ValidationStatus.active || status === ValidationStatus.activeMedicaidInn;
  const isOon = status === ValidationStatus.activeOutOfNetwork || ValidationStatus.activeMedicaidOon;
  const isManual = status === ValidationStatus.manuallyVerified;

  let therapyResponsibility = 'N/A';
  let psychResponsibility = 'N/A';
  let manualResponsibility = 'N/A';

  const responsibilityRange = (followUp: number, intake: number) => {
    return `$${centsToDollars(followUp)} – $${centsToDollars(intake)}`;
  };

  if (isInn) {
    if (showDeductibleRange) {
      therapyResponsibility = responsibilityRange(
        patientResponsibility?.inNetworkTherapyFollowUp ?? 0,
        patientResponsibility?.inNetworkTherapyIntake ?? 0
      );
      psychResponsibility = responsibilityRange(
        patientResponsibility?.inNetworkPsychFollowUp ?? 0,
        patientResponsibility?.inNetworkPsychIntake ?? 0
      );
      return { therapyResponsibility, psychResponsibility, manualResponsibility };
    }
    if (showCoinsuranceAllowable) {
      const allowable = `${coinsurance}% of allowable`;

      therapyResponsibility = allowable;
      psychResponsibility = allowable;
      return { therapyResponsibility, psychResponsibility, manualResponsibility };
    }

    therapyResponsibility = `$${(copay).toFixed(2)}`;
    psychResponsibility = `$${(copay).toFixed(2)}`;

    return { therapyResponsibility, psychResponsibility, manualResponsibility };
  }

  if (isOon) {
    if (showDeductibleRange) {
      therapyResponsibility = responsibilityRange(
        patientResponsibility?.outOfNetworkTherapyFollowUp ?? 0,
        patientResponsibility?.outOfNetworkTherapyIntake ?? 0
      );
      psychResponsibility = responsibilityRange(
        patientResponsibility?.outOfNetworkPsychFollowUp ?? 0,
        patientResponsibility?.outOfNetworkPsychIntake ?? 0
      );
      return { therapyResponsibility, psychResponsibility, manualResponsibility };
    }
    if (showCoinsuranceAllowable) {
      const allowable = `${coinsurance}% of allowable`;

      therapyResponsibility = allowable;
      psychResponsibility = allowable;
      return { therapyResponsibility, psychResponsibility, manualResponsibility };
    }
    therapyResponsibility = `$${(copay).toFixed(2)}`;
    psychResponsibility = `$${(copay).toFixed(2)}`;

    return { therapyResponsibility, psychResponsibility, manualResponsibility };
  }

  if (isManual && patientResponsibility.manualPatientResponsibility) {
    manualResponsibility = `$${centsToDollars(patientResponsibility?.manualPatientResponsibility)}`;
    return { therapyResponsibility, psychResponsibility, manualResponsibility };
  }

  return { therapyResponsibility, psychResponsibility, manualResponsibility };
};

const preloadImage = (src: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.src = src;
    img.onload = () => resolve(src);
    img.onerror = () => reject();
  });
};

export const useFetchImages = (frontUrl: string, backUrl: string, isNonPartnerInsurance = false) => {
  const [ images, setImages ] = React.useState<string[]>([]);
  const [ loading, setLoading ] = React.useState<boolean>(false);

  const fetchImages = useCallback(async () => {
    setLoading(true);

    try {
      const assetReference = isNonPartnerInsurance ? AssetReference.nonPartnerInsurance : AssetReference.insurance;
      const [ front, back ] = await Promise.all([
        frontUrl ? getProtectedAssetUrl(frontUrl, assetReference) : undefined,
        backUrl ? getProtectedAssetUrl(backUrl, assetReference) : undefined,
      ]);

      if (front !== undefined && back !== undefined) {
        const [ preloadedFront, preloadedBack ] = await Promise.all([ preloadImage(front), preloadImage(back) ]);

        setImages([ preloadedFront, preloadedBack ]);
      } else {
        setImages([]);
      }
    } catch (err) {
      console.error(err);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [ frontUrl, backUrl, isNonPartnerInsurance ]);

  useEffect(() => {
    fetchImages();
  }, [ fetchImages ]);

  return { images, loading };
};
